"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
/**
 * It generates [state, setState] pair like useState, but
 * it also updates the state based on changes to value.
 *
 * The transforming functions MUST satisfy the following
 * invariants in order to prevent an infinite loop of updates:
 *
 * (1) valueToState(stateToValue(valueToState(value))) === valueToState(value)
 * (2) stateToValue(valueToState(stateToValue(state))) === stateToValue(state)
 *
 * @template V
 * @template S
 * @param {V} value
 * @param {(v: V) => void} onChange
 * @param {(s: S) => V | undefined} stateToValue
 * @param {(v: V) => S} valueToState
 * @param {boolean} [disableState]
 * @return {[S, React.Dispatch<React.SetStateAction<S>>]}
 */
const useDerivedState = (value, onChange, stateToValue, valueToState, disableState) => {
    const [state, setState] = (0, react_1.useState)(valueToState(value));
    const stateRef = (0, react_1.useRef)(state);
    const valueRef = (0, react_1.useRef)(value);
    (0, react_1.useEffect)(() => {
        stateRef.current = state;
    }, [state]);
    (0, react_1.useEffect)(() => {
        valueRef.current = value;
    }, [value]);
    (0, react_1.useEffect)(() => {
        const newState = valueToState(value);
        if (!disableState &&
            stateToValue(stateRef.current) !== stateToValue(newState)) {
            setState(newState);
        }
    }, [value, stateRef, valueToState, stateToValue]);
    (0, react_1.useEffect)(() => {
        const newValue = stateToValue(state);
        if (!disableState &&
            newValue !== undefined &&
            valueToState(newValue) !== valueToState(value)) {
            onChange(newValue);
        }
    }, [state, valueToState, stateToValue, onChange]);
    (0, react_1.useEffect)(() => {
        if (disableState) {
            setState(valueToState(value));
        }
    }, [value]);
    (0, react_1.useEffect)(() => {
        const newValue = stateToValue(state);
        if (newValue !== undefined && disableState) {
            onChange(newValue);
        }
    }, [state]);
    return [state, setState];
};
exports.default = useDerivedState;
//# sourceMappingURL=index.js.map